import { useEffect, useMemo } from "react";
import type { Email, EmailThread } from "../../../utils/interfaces";
import type {
  ReplyMode,
  ReplySelfContext,
} from "../../../utils/replyRecipients";
import { useMarkReceivablesThreadReadMutation } from "../../../api/queries/receivablesQueries";
import {
  EmailConversationMessages,
  sortEmailsChronologically,
} from "../../emails/EmailConversationMessages";
import { isOutgoingEmail } from "../../emails/emailDirection";
import { EmailMessageBubble } from "./EmailMessageBubble";

export function EmailThreadReadingPane({
  gmailThreadId,
  thread,
  isLoading,
  mailboxContext,
  onReply,
}: {
  gmailThreadId: string | null;
  thread: EmailThread | undefined;
  isLoading: boolean;
  mailboxContext: ReplySelfContext;
  onReply?: (message: Email, mode: ReplyMode) => void;
}) {
  const { mutate: markRead } = useMarkReceivablesThreadReadMutation();

  useEffect(() => {
    if (!gmailThreadId || !thread) return;
    const hasUnread = thread.emails.some((e) => !e.is_read);
    if (hasUnread) {
      markRead(gmailThreadId);
    }
  }, [gmailThreadId, thread, markRead]);

  const sortedEmails = useMemo(
    () => (thread?.emails ? sortEmailsChronologically(thread.emails) : []),
    [thread],
  );

  if (!gmailThreadId) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center bg-slate-50 p-8 text-sm text-slate-500">
        Select a conversation
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center bg-slate-50 p-8 text-sm text-slate-500">
        Loading conversation…
      </div>
    );
  }

  if (!thread || sortedEmails.length === 0) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center bg-slate-50 p-8 text-sm text-slate-500">
        No messages in this thread
      </div>
    );
  }

  const latest = sortedEmails[sortedEmails.length - 1];
  const latestIsOutgoing = isOutgoingEmail(latest, mailboxContext);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-slate-50">
      <header className="border-b border-slate-200 bg-white px-4 py-3">
        <h2 className="text-lg font-semibold text-slate-900">
          {latest.subject}
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          {latestIsOutgoing ? "Sent to" : "From"}{" "}
          {latestIsOutgoing
            ? Array.isArray(latest.to_email)
              ? latest.to_email.join(", ")
              : latest.to_email
            : latest.from_email}
          {latest.when ? ` · ${latest.when}` : ""}
        </p>
      </header>
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <EmailConversationMessages
          emails={thread.emails}
          mailboxContext={mailboxContext}
          onReply={onReply}
          MessageBubble={EmailMessageBubble}
        />
      </div>
    </div>
  );
}
